import { TGoogleRoutePointDirections } from "../../../api/types/Map";
import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import { APP_CONFIG } from "../../../constants/constants";
import {
  getGoogleRoutePointsFromTripPoints,
  renderRouteWithPolylines,
} from "./helper";
import { removeConsecutiveDuplicatePoints } from "./helper-deduplicate";
import { RenderRoute } from "./renderRoute.class";
import { RenderRouteSnapToRoad } from "./route-snaptoroad";

type TDirectionsChunk = {
  origin: google.maps.LatLng;
  destination: google.maps.LatLng;
  waypoints: google.maps.DirectionsWaypoint[];
};

const EPSILON = 1e-5; // ~1 meter

export class RenderRouteDirectionsService extends RenderRoute {
  private preProcessPointsWithSTR = true;

  /**
   * Google Directions API constraints:
   * - Max 25 points (origin + destination + waypoints)
   * - So we keep chunk size smaller for safety
   */
  private MAX_POINTS_PER_REQUEST = 23; // buffer for safety

  /**
   * Step 1: Chunk trip points into valid Directions API requests
   */
  private createChunks(
    points: TGoogleRoutePointDirections[]
  ): TDirectionsChunk[] {
    const chunks: TDirectionsChunk[] = [];

    for (let i = 0; i < points.length - 1; i += this.MAX_POINTS_PER_REQUEST) {
      if (APP_CONFIG.DEBUG.MAPS_GDIRECTIONSSERVICE) {
        console.log(
          `[RNDRT:${this.refString ? `${this.refString}→` : ""}DIR] T#${this.tripIndex} CHUNK i=${i} total_pts=${points.length}`
        );
      }
      const slice = points.slice(i, i + this.MAX_POINTS_PER_REQUEST + 1);

      if (slice.length < 2) continue;

      const origin = new google.maps.LatLng(
        slice[0].location.lat(),
        slice[0].location.lng()
      );

      const destination = new google.maps.LatLng(
        slice[slice.length - 1].location.lat(),
        slice[slice.length - 1].location.lng()
      );

      const waypoints = slice.slice(1, -1).map((point) => ({
        location: new google.maps.LatLng(
          point.location.lat(),
          point.location.lng()
        ),
        stopover: false,
      }));

      chunks.push({ origin, destination, waypoints });
    }

    return chunks;
  }

  /**
   * Step 2: Call Directions API for each chunk
   */
  private fetchDirections(
    chunk: TDirectionsChunk
  ): Promise<google.maps.DirectionsResult> {
    const directionsService = new google.maps.DirectionsService();
    if (APP_CONFIG.DEBUG.MAPS_GDIRECTIONSSERVICE) {
      console.log(
        `[RNDRT:${this.refString ? `${this.refString}→` : ""}DIR] T#${this.tripIndex} REQ origin=${chunk.origin} dest=${chunk.destination} waypts=${chunk.waypoints.length}`,
        chunk.waypoints.map((wp) => wp?.location?.toString())
      );
    }

    return new Promise((resolve, reject) => {
      directionsService.route(
        {
          origin: chunk.origin,
          destination: chunk.destination,
          waypoints: chunk.waypoints,
          travelMode: google.maps.TravelMode.DRIVING,
          optimizeWaypoints: false,
        },
        (result, status) => {
          if (status === google.maps.DirectionsStatus.OK && result) {
            resolve(result);
          } else if (status === google.maps.DirectionsStatus.ZERO_RESULTS) {
            reject(new Error("ZERO_RESULTS"));
          } else {
            reject(status);
          }
        }
      );
    });
  }

  /**
   * Step 3: Pre-render → get merged polyline points
   */
  preRender = async (): Promise<TGoogleRoutePointDirections[]> => {
    const { sanitizedTripPoints, tripIndex } = this;

    const gRoutePoints = removeConsecutiveDuplicatePoints(
      getGoogleRoutePointsFromTripPoints({
        tripPoints: sanitizedTripPoints,
      }) as any as TGoogleRoutePointDirections[],
      EPSILON
    );

    const snapToRoadPoints = this.preProcessPointsWithSTR
      ? await RenderRouteSnapToRoad.getComputedRoutePoints({
          tripIndex,
          sanitizedTripPoints: gRoutePoints.map(
            (point) =>
              ({
                latitude: point.location.lat(),
                longitude: point.location.lng(),
              }) as VehicleMapDataPoint
          ),
        }).catch(() => [])
      : [];

    const pointsForDirections =
      this.preProcessPointsWithSTR && snapToRoadPoints.length > 0
        ? snapToRoadPoints.map(
            (point) =>
              ({
                ...point,
                location: new google.maps.LatLng(
                  point.location.latitude,
                  point.location.longitude
                ),
              }) as any as TGoogleRoutePointDirections
          )
        : gRoutePoints;

    const chunks = this.createChunks(
      pointsForDirections as any as TGoogleRoutePointDirections[]
    );

    if (APP_CONFIG.DEBUG.MAPS_GDIRECTIONSSERVICE) {
      console.log(
        `[RNDRT:${this.refString ? `${this.refString}→` : ""}DIR] T#${tripIndex} CHUNKS total=${chunks.length}`,
        chunks
      );
    }

    const results = await Promise.all(
      chunks.map((chunk) =>
        this.fetchDirections(chunk).catch((err) => {
          if (err instanceof Error && err.message === "ZERO_RESULTS") {
            return null; // skip this chunk
          }
          throw err; // re-throw other errors
        })
      )
    );

    /**
     * Step 4: Extract & merge polyline points
     */
    const mergedPoints: TGoogleRoutePointDirections[] = [];

    results
      .filter(
        (result): result is google.maps.DirectionsResult => result !== null
      )
      .forEach((result) => {
        const route = result.routes[0];

        route.legs.forEach((leg) => {
          leg.steps.forEach((step) => {
            const path = step.path;

            path.forEach((latLng) => {
              mergedPoints.push({
                location: {
                  latitude: latLng.lat(),
                  longitude: latLng.lng(),
                },
              } as any as TGoogleRoutePointDirections);
            });
          });
        });
      });

    return mergedPoints;
  };

  /**
   * Step 5: Render (same as SnapToRoad)
   */
  render = async () => {
    const directionsPoints = await this.preRender();

    const { props, tripIndex, polylineConfig } = this;

    const routeVisibility =
      props.mapRef.current.routeViewsEnabled.DIRECTIONS_SERVICE;

    if (
      props?.selectedTripIndex !== null &&
      tripIndex !== props?.selectedTripIndex
    ) {
      return;
    }

    // too less points to render a route
    if (!directionsPoints || directionsPoints.length < 2) {
      if (APP_CONFIG.DEBUG.MAPS_GDIRECTIONSSERVICE) {
        console.error(
          `[RNDRT:${this.refString ? `${this.refString}→` : ""}DIR] T#${tripIndex} ERR not enough points to render`
        );
      }
      this.isError = true; // set error flag to true
      return;
    }

    const mapElements = renderRouteWithPolylines({
      props,
      tripIndex,
      polylineConfig,
      gRoutePoints: directionsPoints.map((point) => ({
        ...point,
        location: new google.maps.LatLng(
          (point.location as any).latitude,
          (point.location as any).longitude
        ) as any,
      })),
      routeVisibility,
    });

    this.setMapElements(mapElements);

    props.mapRef.current.objects.mTripsVisibility.push(routeVisibility ? 1 : 0);

    this.isInitialized = true; // set initialized flag to true after first render
  };

  /**
   * Static method to get computed route points using Directions API without needing
   * to render the route, useful for other use cases where we just need the directions
   * points for a given set of trip points
   */
  static getComputedRoutePoints = async ({
    tripIndex,
    sanitizedTripPoints,
    refString = "",
  }: {
    tripIndex: number;
    sanitizedTripPoints: VehicleMapDataPoint[];
    refString?: string; // optional string to identify the caller in debug logs
  }): Promise<TGoogleRoutePointDirections[]> => {
    const instance = new RenderRouteDirectionsService({
      props: {} as any,
      tripIndex,
      sanitizedTripPoints,
      interpolatedTripPoints: [],
      // any config values - as we are only interested in the preRender method of this class
      polylineConfig: {
        strokeColor: "#0000FF",
        strokeOpacity: 0,
        strokeWeight: 0,
      },
      refString,
    });
    // Fails soft: a Directions error other than ZERO_RESULTS (quota, network,
    // bad request) resolves to an empty array rather than rejecting, so the
    // render orchestrator can step down to another method instead of the
    // whole trip render throwing. ZERO_RESULTS already resolves to [] inside
    // preRender.
    try {
      return await instance.preRender();
    } catch (error) {
      if (APP_CONFIG.DEBUG.MAPS_GDIRECTIONSSERVICE)
        console.warn(
          `[RNDRT:${refString ? `${refString}→` : ""}DIR] T#${tripIndex} ERR - resolving empty so caller can fall back`,
          error
        );
      return [];
    }
  };
}
